import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';
import { Heart, Target, Lightbulb } from 'lucide-react';

interface Props { onBack: () => void; }

export default function AboutPage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';

  return (
    <StaticPageLayout title={en ? 'About' : '关于我们'} onBack={onBack}>
      <Helmet>
        <title>{en ? 'About - MinutesAI' : '关于我们 - MinutesAI'}</title>
        <meta name="description" content={en ? 'MinutesAI is an AI-powered meeting intelligence platform. Learn about our mission to transform how teams capture and act on meeting insights.' : 'MinutesAI 是 AI 驱动的会议智能平台。了解我们变革团队捕获和执行会议洞察方式的使命。'} />
        <link rel="canonical" href="https://minutesai.com/about" />
        <meta property="og:title" content={en ? 'About - MinutesAI' : '关于我们 - MinutesAI'} />
        <meta property="og:url" content="https://minutesai.com/about" />
      </Helmet>
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-brand-900 font-serif mb-4">
          {en ? 'About Minutes AI' : '关于 Minutes AI'}
        </h2>
        <p className="text-lg text-ink-secondary max-w-2xl mx-auto">
          {en ? 'We believe every meeting deserves to be captured with precision and clarity.' : '我们相信每一场会议都值得被精准、清晰地记录。'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        <div className="bg-white rounded-2xl border border-surface-border p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-900/10 mb-4">
            <Target size={24} className="text-brand-900" />
          </div>
          <h3 className="text-xl font-bold text-brand-900 mb-2">{en ? 'Our Mission' : '我们的使命'}</h3>
          <p className="text-ink-secondary">
            {en ? 'To transform how teams capture, understand, and act on meeting intelligence with AI-powered precision.' : '用 AI 驱动的精准能力，变革团队捕获、理解和执行会议信息的方式。'}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-border p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-900/10 mb-4">
            <Lightbulb size={24} className="text-brand-900" />
          </div>
          <h3 className="text-xl font-bold text-brand-900 mb-2">{en ? 'Our Vision' : '我们的愿景'}</h3>
          <p className="text-ink-secondary">
            {en ? 'A world where no critical insight is lost in meetings, and every decision is backed by accurate records.' : '一个不会在会议中遗失任何关键洞察、每个决策都有准确记录支撑的世界。'}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-border p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-900/10 mb-4">
            <Heart size={24} className="text-brand-900" />
          </div>
          <h3 className="text-xl font-bold text-brand-900 mb-2">{en ? 'Our Values' : '我们的价值观'}</h3>
          <p className="text-ink-secondary">
            {en ? 'Precision, trust, and efficiency. We hold ourselves to the highest standards of accuracy and reliability.' : '精准、信任、高效。我们以最高标准要求自己的准确性和可靠性。'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-surface-border p-12 text-center">
        <h3 className="text-2xl font-bold text-brand-900 font-serif mb-4">
          {en ? 'Built by a team that cares' : '由一支用心的团队打造'}
        </h3>
        <p className="text-ink-secondary max-w-2xl mx-auto">
          {en ? 'Our team combines deep expertise in AI, natural language processing, and enterprise software to deliver a meeting intelligence platform that professionals can trust.' : '我们的团队融合了 AI、自然语言处理和企业软件领域的深厚专业知识，打造出专业人士可以信赖的会议智能平台。'}
        </p>
      </div>
    </StaticPageLayout>
  );
}
